if (!customElements.get('pythal-size-chart')) {
  const PDFJS_VERSION = '3.11.174';
  const PDFJS_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;
  let pdfjsPromise;

  // pdf.js is only fetched the first time a PDF chart is opened.
  const loadPdfjs = () => {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    if (pdfjsPromise) return pdfjsPromise;

    pdfjsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${PDFJS_BASE}/pdf.min.js`;
      script.async = true;
      script.onload = () => {
        if (!window.pdfjsLib) return reject(new Error('pdf.js missing'));
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
        resolve(window.pdfjsLib);
      };
      script.onerror = () => reject(new Error('pdf.js failed to load'));
      document.head.appendChild(script);
    }).catch((error) => {
      pdfjsPromise = null;
      throw error;
    });

    return pdfjsPromise;
  };

  // Finds the printed area of a page so blank margins don't eat screen width.
  const findContentBox = async (page) => {
    const probeScale = 0.6;
    const viewport = page.getViewport({ scale: probeScale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d', { willReadFrequently: true });
    await page.render({ canvasContext: context, viewport }).promise;

    const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const i = (y * width + x) * 4;
        if (data[i + 3] > 0 && (data[i] < 245 || data[i + 1] < 245 || data[i + 2] < 245)) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    const full = page.getViewport({ scale: 1 });
    if (maxX < 0) return { x: 0, y: 0, width: full.width, height: full.height };

    const pad = 10;
    const x = Math.max(0, minX / probeScale - pad);
    const y = Math.max(0, minY / probeScale - pad);
    return {
      x,
      y,
      width: Math.min(full.width, (maxX + 1) / probeScale + pad) - x,
      height: Math.min(full.height, (maxY + 1) / probeScale + pad) - y,
    };
  };

  customElements.define(
    'pythal-size-chart',
    class PythalSizeChart extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.dialog = this.querySelector('[data-size-chart-dialog]');
        this.openButton = this.querySelector('[data-size-chart-open]');
        this.closeButton = this.querySelector('[data-size-chart-close]');
        this.frame = this.querySelector('[data-size-chart-frame]');
        this.pages = this.querySelector('[data-size-chart-pages]');
        this.viewer = this.querySelector('.pythal-size-chart__viewer');

        if (!this.dialog || !this.openButton || !this.closeButton) return;

        this.dataset.initialized = 'true';
        this.openButton.addEventListener('click', () => this.open());
        this.closeButton.addEventListener('click', () => this.close());
        this.dialog.addEventListener('close', () => this.unlockPage());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });

        this.handleResize = () => {
          if (!this.dialog.open) return;
          window.clearTimeout(this.resizeTimer);
          this.resizeTimer = window.setTimeout(() => {
            if (this.pdfRendered) {
              this.renderPdf();
            } else if (this.frame && this.frame.hasAttribute('src')) {
              this.loadFrame(true);
            }
          }, 180);
        };
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('orientationchange', this.handleResize);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('orientationchange', this.handleResize);
        window.clearTimeout(this.resizeTimer);
        window.cancelAnimationFrame(this.frameRequest);
      }

      // Fallback: the browser's own PDF viewer.
      loadFrame(forceReload = false) {
        if (!this.frame || !this.frame.dataset.src) return;

        this.viewer?.classList.remove('is-rendered', 'is-loading');
        window.cancelAnimationFrame(this.frameRequest);
        this.frameRequest = window.requestAnimationFrame(() => {
          this.frameRequest = window.requestAnimationFrame(() => {
            if (forceReload) this.frame.removeAttribute('src');
            if (!this.frame.hasAttribute('src')) this.frame.src = this.frame.dataset.src;
          });
        });
      }

      // Draws each PDF page as a canvas fitted to the modal width, so the table
      // text is readable without pinch-zooming.
      async renderPdf() {
        const url = this.pages?.dataset.pdfUrl;
        if (!url) return this.loadFrame();

        const renderId = (this.renderId || 0) + 1;
        this.renderId = renderId;
        if (!this.pdfRendered) this.viewer.classList.add('is-loading');

        try {
          const pdfjs = await loadPdfjs();
          if (!this.pdfDocument) {
            this.pdfDocument = await pdfjs.getDocument({ url, isEvalSupported: false }).promise;
          }

          const pdf = this.pdfDocument;
          const styles = window.getComputedStyle(this.pages);
          const availableWidth =
            this.pages.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
          const pixelRatio = Math.min(window.devicePixelRatio || 1, 3);
          const canvases = [];

          for (let number = 1; number <= pdf.numPages; number += 1) {
            const page = await pdf.getPage(number);
            if (!page.pythalContentBox) page.pythalContentBox = await findContentBox(page);
            const box = page.pythalContentBox;

            const cssScale = availableWidth / box.width;
            const scale = cssScale * pixelRatio;
            const viewport = page.getViewport({ scale, offsetX: -box.x * scale, offsetY: -box.y * scale });

            const canvas = document.createElement('canvas');
            canvas.className = 'pythal-size-chart__page';
            canvas.width = Math.floor(box.width * scale);
            canvas.height = Math.floor(box.height * scale);
            canvas.style.width = `${availableWidth}px`;
            canvas.style.aspectRatio = `${box.width} / ${box.height}`;

            await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
            if (renderId !== this.renderId) return;
            canvases.push(canvas);
          }

          this.pages.replaceChildren(...canvases);
          this.pdfRendered = true;
          this.viewer.classList.remove('is-loading');
          this.viewer.classList.add('is-rendered');
        } catch (error) {
          if (renderId !== this.renderId) return;
          this.pdfRendered = false;
          this.loadFrame();
        }
      }

      open() {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        document.documentElement.classList.add('pythal-size-chart-modal-open');

        // Render only after the dialog has its final dimensions.
        if (!this.pages) return this.loadFrame();
        window.requestAnimationFrame(() => {
          this.pages.scrollTop = 0;
          this.renderPdf();
        });
      }

      close() {
        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.unlockPage();
        }

        this.openButton.focus({ preventScroll: true });
      }

      unlockPage() {
        document.documentElement.classList.remove('pythal-size-chart-modal-open');
      }
    }
  );
}
